import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, Loader2, CalendarPlus } from "lucide-react";

export default function ExtendVacancy() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("idle");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [days, setDays] = useState(30);
  const ran = useRef(false);

  const submit = async () => {
    if (!token) { setError("Missing token"); setStatus("error"); return; }
    setStatus("loading");
    try {
      const r = await api.post("/vacancies/extend", { token, days });
      setData(r.data);
      setStatus("success");
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't extend right now.");
      setStatus("error");
    }
  };

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
  }, []);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center bg-card border border-border rounded-lg p-10">
        <Link to="/" className="text-xs uppercase tracking-[0.2em] text-ink font-semibold">← Railway Careers UK</Link>
        {status === "idle" && (
          <>
            <CalendarPlus className="w-12 h-12 text-amber-600 mx-auto mt-6" />
            <h1 className="font-display font-extrabold text-ink text-2xl mt-5">Extend your vacancy</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Push this vacancy's expiry date out by the number of days below. No sign-in required.
            </p>
            <div className="mt-6">
              <label className="text-xs uppercase tracking-[0.18em] font-semibold text-ink">Extend by</label>
              <div className="flex items-center justify-center gap-2 mt-2">
                <input
                  type="number"
                  min={1}
                  max={180}
                  value={days}
                  onChange={(e) => setDays(parseInt(e.target.value) || 30)}
                  className="w-24 text-center text-2xl font-mono font-bold border border-border rounded px-3 py-2 bg-paper"
                  data-testid="extend-days-input"
                />
                <span className="text-sm text-muted-foreground">days</span>
              </div>
            </div>
            <button
              onClick={submit}
              data-testid="extend-submit"
              className="inline-block mt-6 px-6 py-3 rounded-full bg-navy text-bone font-semibold hover:bg-lime-600 hover:text-ink transition-colors"
            >
              Extend vacancy
            </button>
          </>
        )}
        {status === "loading" && <Loader2 className="w-12 h-12 text-amber-600 mx-auto mt-6 animate-spin" />}
        {status === "success" && data && (
          <>
            <CheckCircle2 className="w-12 h-12 text-lime-600 mx-auto mt-6" data-testid="extend-success" />
            <h1 className="font-display font-extrabold text-ink text-2xl mt-5">Extended</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              New expiry date: <span className="font-mono font-semibold text-ink">{data.new_expiry}</span>
              <br />(was {data.old_expiry})
            </p>
            <p className="mt-4 text-xs text-muted-foreground">
              Need a different number of days? Sign in to your dashboard for full edit access.
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-12 h-12 text-red-500 mx-auto mt-6" data-testid="extend-error" />
            <h1 className="font-display font-extrabold text-ink text-2xl mt-5">Couldn't extend</h1>
            <p className="mt-3 text-sm text-muted-foreground">{error}</p>
            <Link to="/employer-portal/login" className="inline-block mt-6 px-5 py-2.5 rounded-full bg-navy text-bone font-semibold hover:bg-lime-600 hover:text-ink transition-colors">
              Sign in to dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
